
import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { 
  ReferralId, 
  ReferralIdStats,
  ReferralIdBankStats,
  ReferralIdStatus
} from '@/utils/referral/bank/types';

export const useMasterLicenseReferralBank = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [referralIds, setReferralIds] = useState<ReferralId[]>([]);
  const [bankStats, setBankStats] = useState<ReferralIdBankStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Fetch referral IDs from the bank
  const fetchReferralIds = useCallback(async () => {
    setIsLoading(true);
    try {
      // In a real app, this would call an API
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Generate mock referral IDs
      const mockReferralIds: ReferralId[] = Array.from({ length: 20 }, (_, i) => ({
        id: `ref-${Date.now()}-${i}`,
        code: `REF${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
        userId: i < 5 ? `user-${i}` : '',
        instanceId: 'default-instance',
        status: i < 5 ? 'active' as ReferralIdStatus : 'unused' as ReferralIdStatus,
        createdAt: new Date(),
        updatedAt: new Date(),
        isAssigned: i < 5,
        assignedAt: i < 5 ? new Date() : undefined
      }));
      
      setReferralIds(mockReferralIds);
      
      // Generate mock bank stats
      const mockBankStats: ReferralIdBankStats = {
        total: mockReferralIds.length,
        used: mockReferralIds.filter(id => id.isAssigned).length,
        available: mockReferralIds.filter(id => !id.isAssigned).length,
        pending: 0,
        totalIds: mockReferralIds.length,
        assignedIds: mockReferralIds.filter(id => id.isAssigned).length,
        unassignedIds: mockReferralIds.filter(id => !id.isAssigned).length,
        instanceAllocations: {
          'default-instance': mockReferralIds.length
        }
      };
      
      setBankStats(mockBankStats);
      setError(null);
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to fetch referral IDs');
      setError(errorObj);
      toast({
        title: 'Error',
        description: errorObj.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Generate new referral IDs
  const generateReferralIdBatch = useCallback(
    async (instanceId: string, count: number = 10, prefix: string = 'REF') => {
      setIsGenerating(true);
      try {
        // In a real app, this would call an API
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Generate new referral IDs
        const newReferralIds: ReferralId[] = Array.from({ length: count }, (_, i) => ({
          id: `ref-${Date.now()}-${i}`,
          code: `${prefix}${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
          userId: '',
          instanceId,
          status: 'unused' as ReferralIdStatus,
          createdAt: new Date(),
          updatedAt: new Date(),
          isAssigned: false
        }));
        
        // Update state with new referral IDs
        setReferralIds(prev => [...newReferralIds, ...prev]);
        
        // Update bank stats
        if (bankStats) {
          setBankStats({
            ...bankStats,
            total: bankStats.total + count,
            available: bankStats.available + count,
            totalIds: bankStats.totalIds + count,
            unassignedIds: bankStats.unassignedIds + count,
            instanceAllocations: {
              ...bankStats.instanceAllocations,
              [instanceId]: (bankStats.instanceAllocations[instanceId] || 0) + count
            }
          });
        }
        
        toast({
          title: 'Success',
          description: `Generated ${count} new referral IDs`,
        });
        
        return newReferralIds.map(id => id.code);
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error('Failed to generate referral IDs');
        setError(errorObj);
        toast({
          title: 'Error',
          description: errorObj.message,
          variant: 'destructive',
        });
        return [];
      } finally {
        setIsGenerating(false);
      }
    },
    [bankStats, toast]
  );

  // Get referral ID stats
  const getReferralIdStats = useCallback(async (): Promise<ReferralIdStats> => {
    try {
      // In a real app, this would call an API
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Return stats from state
      return {
        total: bankStats?.total || 0,
        used: bankStats?.used || 0,
        available: bankStats?.available || 0,
        pending: bankStats?.pending || 0
      };
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to get referral ID stats');
      setError(errorObj);
      throw errorObj;
    }
  }, [bankStats]);

  // Handle generating more IDs
  const handleGenerateMoreIds = useCallback(
    async (count: number = 20, prefix: string = 'REF') => {
      await generateReferralIdBatch('default-instance', count, prefix);
      
      toast({
        title: 'Referral IDs Generated',
        description: `${count} more referral IDs have been added to the bank.`,
      });
    },
    [generateReferralIdBatch, toast]
  );

  // Check if bank needs replenishing and handle it
  const handleCheckAndReplenish = useCallback(async () => {
    if (bankStats && bankStats.available < 10) {
      await handleGenerateMoreIds(20);
      
      toast({
        title: 'Referral IDs Replenished',
        description: 'The referral ID bank was running low and has been replenished.',
      });
    } else {
      toast({
        title: 'Sufficient IDs Available',
        description: 'The referral ID bank has sufficient IDs available.',
      });
    }
  }, [bankStats, handleGenerateMoreIds, toast]);

  // Load data when component mounts
  useEffect(() => {
    fetchReferralIds();
  }, [fetchReferralIds]);

  return {
    generateReferralIdBatch,
    getReferralIdStats,
    isLoading,
    isGenerating,
    error,
    referralIds,
    bankStats,
    handleGenerateMoreIds,
    handleCheckAndReplenish,
    refreshData: fetchReferralIds,
    user
  };
};
